import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  Database as DatabaseIcon,
  Plug as PlugIcon,
  Tag as TagIcon,
  Workflow as LineIcon
} from 'lucide-react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import {
  decodeRouteParam,
  PLANT_COMPUTE_DB_QUERY_PARAM,
  PLANT_COMPUTE_PROVIDER_QUERY_PARAM,
  PLANT_COMPUTE_TAG_QUERY_PARAM
} from '@atbash-labs/plantview-links'
import {
  useGetPlantComputeDetailQuery,
  usePlantSessionPaint,
  collectAdditionalProperties,
  isQueryablePlantDatabase,
  type PlantComputeHost,
  type PlantComputeRef,
  type PlantControllerRef,
  type PlantDatabase,
  type PlantMonitoredAsset,
  type PlantTagProvider
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import { cn } from '../../lib/utils'
import { plantComputeIndexHref } from '../../lib/plant-links'
import { computeIconFor, computeLabelFor, computeTypeKey } from './compute-icon'
import { equipmentIconFor } from './equipment-icon'
import { DatabaseQueryEngine } from './DatabaseQueryEngine'
import { EntityDetailsInspector } from './entity/EntityDetailsInspector'
import { PlantStrayPill } from './entity/PlantStrayPill'
import { PlantHref } from './PlantHref'
import { TagProviderBrowser } from './TagProviderBrowser'
import { MtConnectAgentActions } from './MtConnectAgentActions'
import {
  SessionGhostTile,
  SessionMarkAffordances,
  SessionTitleMark,
  sessionDetailHeaderClass,
  sessionMarkClass,
  useVisibleGhosts,
  type PlantSessionGhostView
} from './session-mark-ui'

/**
 * `/plant/compute/hosts/:computeId` — single compute host.
 *
 * Renders header (icon + name + type + URL/version/vendor), then the
 * relevant child sections for that host's type:
 *   - Gateways (`ignition` / `aveva`): tag providers + databases
 *     (JDBC catalogs may live on a linked `db_server` Compute; the
 *     detail cypher walks `NETWORKS_WITH` so they still appear here)
 *   - OPC servers: tag providers only
 *   - Database servers: databases only — including standalone dump
 *     `:Database` nodes (`urn:db:<name>`) projected as `db_server` hosts
 *   - Other: both
 *
 * Queryable database cards open the inline query engine (`?db=`).
 *
 * When a session is painting, the header / cards carry marks and
 * deleted providers/databases render as ghost tiles under their grids.
 */
const ComputeHostDetailPage = (): React.ReactElement => {
  const { computeId = '' } = useParams<{ computeId: string }>()
  const decodedComputeId = decodeRouteParam(computeId)

  const { data, isLoading, isError } = useGetPlantComputeDetailQuery(decodedComputeId, {
    skip: !decodedComputeId
  })

  const host = data?.host
  const tagProviders = React.useMemo(() => data?.tagProviders ?? [], [data?.tagProviders])
  const databases = React.useMemo(() => data?.databases ?? [], [data?.databases])
  const providerGhosts = useVisibleGhosts(decodedComputeId, 'TagProvider')
  const databaseGhosts = useVisibleGhosts(decodedComputeId, 'Database')

  const [searchParams, setSearchParams] = useSearchParams()
  const providerParam = searchParams.get(PLANT_COMPUTE_PROVIDER_QUERY_PARAM)
  // Ignore a stale `?provider=` from a previous host's URL — only honor
  // the param when it identifies one of *this* host's tag providers.
  const openProviderId = React.useMemo(() => {
    if (!providerParam) return null
    return tagProviders.some((p) => p.id === providerParam) ? providerParam : null
  }, [providerParam, tagProviders])

  const toggleProvider = React.useCallback(
    (providerId: string) => {
      const next = new URLSearchParams(searchParams)
      if (next.get(PLANT_COMPUTE_PROVIDER_QUERY_PARAM) === providerId) {
        next.delete(PLANT_COMPUTE_PROVIDER_QUERY_PARAM)
      } else {
        next.set(PLANT_COMPUTE_PROVIDER_QUERY_PARAM, providerId)
        // Mutually exclusive with the database drilldown.
        next.delete(PLANT_COMPUTE_DB_QUERY_PARAM)
      }
      // The `?tag=` deep-link only makes sense in the context of a
      // specific provider — once the user manually toggles to a
      // different provider (or closes the panel) we drop it so the
      // browser resets to its blank flat-or-explorer view.
      next.delete(PLANT_COMPUTE_TAG_QUERY_PARAM)
      setSearchParams(next)
    },
    [searchParams, setSearchParams]
  )

  // SCADA-tag deep-link from PLC pages: when set, the open provider's
  // browser pre-fills its search with this name and switches to flat
  // view so the row is immediately on-screen. Only honored when it
  // pairs with a valid `?provider=` for *this* host (same staleness
  // guard as `openProviderId` above).
  const tagParam = searchParams.get(PLANT_COMPUTE_TAG_QUERY_PARAM)
  const selectedTagName = openProviderId && tagParam ? tagParam : null

  const dbParam = searchParams.get(PLANT_COMPUTE_DB_QUERY_PARAM)
  // Same stale-param guard as `openProviderId` — only honor `?db=` when
  // it refers to one of *this* host's databases.
  const openDatabaseId = React.useMemo(() => {
    if (!dbParam) return null
    return databases.some((d) => d.id === dbParam) ? dbParam : null
  }, [dbParam, databases])

  const toggleDb = React.useCallback(
    (databaseId: string) => {
      const next = new URLSearchParams(searchParams)
      if (next.get(PLANT_COMPUTE_DB_QUERY_PARAM) === databaseId) {
        next.delete(PLANT_COMPUTE_DB_QUERY_PARAM)
      } else {
        next.set(PLANT_COMPUTE_DB_QUERY_PARAM, databaseId)
        next.delete(PLANT_COMPUTE_PROVIDER_QUERY_PARAM)
        next.delete(PLANT_COMPUTE_TAG_QUERY_PARAM)
      }
      setSearchParams(next)
    },
    [searchParams, setSearchParams]
  )

  const setActiveDatabase = React.useCallback(
    (databaseId: string) => {
      const next = new URLSearchParams(searchParams)
      next.set(PLANT_COMPUTE_DB_QUERY_PARAM, databaseId)
      next.delete(PLANT_COMPUTE_PROVIDER_QUERY_PARAM)
      next.delete(PLANT_COMPUTE_TAG_QUERY_PARAM)
      setSearchParams(next)
    },
    [searchParams, setSearchParams]
  )

  const selectHost = React.useCallback(() => {
    const next = new URLSearchParams(searchParams)
    next.delete(PLANT_COMPUTE_PROVIDER_QUERY_PARAM)
    next.delete(PLANT_COMPUTE_TAG_QUERY_PARAM)
    next.delete(PLANT_COMPUTE_DB_QUERY_PARAM)
    setSearchParams(next)
  }, [searchParams, setSearchParams])

  const typeKey = computeTypeKey(host?.compute_type)
  const showTagProviders = typeKey !== 'dbserver'
  const showDatabases = typeKey !== 'opc'
  // The DB query engine has three backends: Ignition WebDev (any
  // database mounted on an Ignition gateway), a direct Postgres
  // executor on the proxy, and DuckDB dumps (`database_type: DUCKDB`
  // / `duckdb://`). Cards stay static for catalogs that match none.
  const canQueryDatabase = React.useCallback(
    (database: PlantDatabase): boolean => isQueryablePlantDatabase(database, host?.compute_type),
    [host?.compute_type]
  )
  const activeDatabase = React.useMemo(
    () => (openDatabaseId ? databases.find((d) => d.id === openDatabaseId) : undefined),
    [databases, openDatabaseId]
  )
  // Selecting a tag provider rebinds the page footer (provenance, session
  // property changes, entity details) to that provider instead of the host.
  const openProvider = React.useMemo(
    () => (openProviderId ? tagProviders.find((p) => p.id === openProviderId) : undefined),
    [openProviderId, tagProviders]
  )
  const inspectorEntity = openProvider ?? host
  const additionalProperties = React.useMemo(() => {
    if (openProvider) {
      return collectAdditionalProperties(
        'TagProvider',
        openProvider as unknown as Record<string, unknown>
      )
    }
    return host
      ? collectAdditionalProperties('Compute', host as unknown as Record<string, unknown>)
      : []
  }, [host, openProvider])

  return (
    <div className="flex flex-1 flex-col gap-card">
      <nav className="text-sm text-ink-3" aria-label="Breadcrumb">
        <Link to={plantComputeIndexHref()} className="hover:text-ink hover:underline">
          Compute
        </Link>
        <span className="mx-1.5 text-ink-4">/</span>
        <span className="text-ink">{host?.host || host?.name || decodedComputeId}</span>
      </nav>

      {isError ? (
        <DetailFallback
          title="Couldn't load this compute host"
          hint="The host may have been deleted or the proxy is unreachable."
        />
      ) : isLoading || !data ? (
        <DetailFallback title="Loading compute host…" hint="" muted />
      ) : (
        <>
          <HostHeader
            host={data.host}
            isStray={data.isStray}
            gateways={data.gateways}
            controllers={data.controllers}
            dcsControllers={data.dcsControllers}
            selected={!openProviderId && !openDatabaseId}
            onSelect={openProviderId || openDatabaseId ? selectHost : undefined}
          />

          <MonitoredAssetsSection assets={data.monitoredAssets} />

          {showTagProviders && (
            <section>
              <SectionHeader
                label="Tag providers"
                subtitle={`${tagProviders.length + providerGhosts.length} ${
                  tagProviders.length + providerGhosts.length === 1 ? 'provider' : 'providers'
                }`}
              />
              <div className="flex flex-col gap-card">
                {tagProviders.length === 0 && providerGhosts.length === 0 ? (
                  <div className="rounded-xl border bg-card p-4 text-sm text-ink-3 shadow-sm">
                    This host has no tag providers.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(17.5rem,1fr))]">
                    {tagProviders.map((provider) => (
                      <TagProviderCard
                        key={provider.id}
                        provider={provider}
                        active={openProviderId === provider.id}
                        onClick={() => toggleProvider(provider.id)}
                      />
                    ))}
                    {providerGhosts.map((ghost) => (
                      <ComputeGhostTile key={ghost.id} ghost={ghost} kind="provider" />
                    ))}
                  </div>
                )}
                {openProvider && (
                  <>
                    {openProvider.protocol === 'mtconnect' && openProvider.url ? (
                      <MtConnectAgentActions
                        agentUrl={openProvider.url}
                        computeUrn={data.host.id}
                      />
                    ) : null}
                    <TagProviderBrowser
                      host={data.host}
                      provider={openProvider}
                      selectedTagName={selectedTagName}
                    />
                  </>
                )}
              </div>
            </section>
          )}

          {showDatabases && (
            <section>
              <SectionHeader
                label="Databases"
                subtitle={`${databases.length + databaseGhosts.length} ${
                  databases.length + databaseGhosts.length === 1 ? 'database' : 'databases'
                }`}
              />
              <div className="flex flex-col gap-card">
                {databases.length === 0 && databaseGhosts.length === 0 ? (
                  <div className="rounded-xl border bg-card p-4 text-sm text-ink-3 shadow-sm">
                    This host has no databases.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(17.5rem,1fr))]">
                    {databases.map((database) => (
                      <DatabaseCard
                        key={database.id}
                        database={database}
                        active={openDatabaseId === database.id}
                        onClick={
                          canQueryDatabase(database) ? () => toggleDb(database.id) : undefined
                        }
                      />
                    ))}
                    {databaseGhosts.map((ghost) => (
                      <ComputeGhostTile key={ghost.id} ghost={ghost} kind="database" />
                    ))}
                  </div>
                )}
                {openDatabaseId && activeDatabase && canQueryDatabase(activeDatabase) && (
                  <DatabaseQueryEngine
                    host={data.host}
                    databases={databases.filter(canQueryDatabase)}
                    activeDatabaseId={openDatabaseId}
                    onActiveDatabaseChange={setActiveDatabase}
                  />
                )}
              </div>
            </section>
          )}

          {inspectorEntity && (
            <EntityDetailsInspector
              entity={inspectorEntity}
              additionalProperties={additionalProperties}
            />
          )}
        </>
      )}
    </div>
  )
}

function HostHeader({
  host,
  isStray,
  gateways,
  controllers,
  dcsControllers,
  selected,
  onSelect
}: {
  host: PlantComputeHost
  isStray: boolean
  gateways: PlantComputeRef[]
  controllers: PlantControllerRef[]
  dcsControllers: PlantControllerRef[]
  /** True when the host (not a provider/database) owns the detail chrome. */
  selected: boolean
  /** Clears provider/database selection so the host is selected again. */
  onSelect?: () => void
}): React.ReactElement {
  const Icon = computeIconFor(host.compute_type)
  const typeLabel = computeLabelFor(host.compute_type)
  const isDevice = computeTypeKey(host.compute_type) === 'device'
  const showMetaRow = Boolean(
    host.version || host.vendor || host.opc_security_mode || (isDevice && host.device_driver)
  )
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(host.id)

  // `host` is the canonical hostname per the SCADA spec; `name` is the
  // human-facing identifier set at ingest. When both are present, use
  // `host` as the title and surface the rest as labeled rows so the
  // reader can tell each mono value apart.
  const title = host.name || host.host
  const addressRows: Array<{ label: string; value: string }> = []
  // Devices URN-collapse on `host:port` and we already render
  // `host.url` (= `host:port`) below as "Address" — surfacing the
  // bare `host` separately on the same card would be redundant.
  // Non-device computes keep `host` as a distinct hostname row when
  // it differs from the URL so AVEVA / OPC / DB-server cards can
  // show both DNS and connection string.
  if (!isDevice && host.host && host.host !== host.url) {
    addressRows.push({ label: 'Hostname', value: host.host })
  }
  if (host.url) {
    addressRows.push({ label: isDevice ? 'Address' : 'Host URL', value: host.url })
  }
  if (host.opc_endpoint) {
    addressRows.push({ label: 'Endpoint', value: host.opc_endpoint })
  }

  return (
    <header
      className={cn(
        sessionDetailHeaderClass(
          'flex items-start gap-4 rounded-xl border bg-card p-5 text-card-foreground shadow-sm transition-shadow',
          mark
        ),
        // Inset brand ring — stays visible over session-paint fills, no offset gap.
        selected && 'ring-2 ring-inset ring-brand',
        onSelect &&
          'cursor-pointer hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand'
      )}
      aria-current={selected ? 'true' : undefined}
      {...(onSelect
        ? {
            role: 'button' as const,
            tabIndex: 0,
            onClick: onSelect,
            onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onSelect()
              }
            }
          }
        : {})}
    >
      <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
        <Icon size={22} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="m-0 text-2xl font-semibold text-ink">{title}</h1>
          <SessionTitleMark mark={mark} />
          <Badge variant="secondary">{typeLabel}</Badge>
          {host.kind && <Badge variant="outline">{host.kind}</Badge>}
          <PlantStrayPill isStray={isStray} />
        </div>
        {addressRows.length > 0 && (
          <dl className="mt-2 flex flex-col gap-1">
            {addressRows.map(({ label, value }) => (
              <div key={label} className="flex flex-wrap items-baseline gap-2 text-xs">
                <dt className="min-w-[5rem] text-10 font-semibold uppercase tracking-[0.06em] text-ink-4">
                  {label}
                </dt>
                <dd className="m-0 break-all font-mono text-ink-3">{value}</dd>
              </div>
            ))}
          </dl>
        )}
        {showMetaRow && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {host.version && <Badge variant="outline">v{host.version}</Badge>}
            {host.vendor && <Badge variant="outline">{host.vendor}</Badge>}
            {host.opc_security_mode && <Badge variant="outline">{host.opc_security_mode}</Badge>}
            {isDevice && host.device_driver && (
              <Badge variant="outline">{host.device_driver}</Badge>
            )}
          </div>
        )}
        {gateways.length > 0 && (
          <div className="mt-3 flex flex-wrap items-baseline gap-2 text-xs">
            <span className="text-10 font-semibold uppercase tracking-[0.06em] text-ink-4">
              Part of
            </span>
            <span className="flex flex-wrap gap-1.5">
              {gateways.map((gw) => (
                <PlantHref
                  key={gw.id}
                  id={gw.id}
                  label="Compute"
                  node={gw}
                  onClick={(event) => event.stopPropagation()}
                  className="rounded-md border border-transparent bg-bg-sunk px-2 py-0.5 font-medium text-ink-3 no-underline hover:border-brand hover:text-ink"
                >
                  {gw.name}
                </PlantHref>
              ))}
            </span>
          </div>
        )}
        {controllers.length > 0 && (
          <div className="mt-3 flex flex-wrap items-baseline gap-2 text-xs">
            <span className="text-10 font-semibold uppercase tracking-[0.06em] text-ink-4">
              PLC controller
            </span>
            <span className="flex flex-wrap gap-1.5">
              {controllers.map((ctrl) => (
                <PlantHref
                  key={ctrl.id}
                  id={ctrl.id}
                  label="Controller"
                  node={ctrl}
                  onClick={(event) => event.stopPropagation()}
                  className="rounded-md border border-transparent bg-bg-sunk px-2 py-0.5 font-medium text-ink-3 no-underline hover:border-brand hover:text-ink"
                >
                  {ctrl.name}
                </PlantHref>
              ))}
            </span>
          </div>
        )}
        {dcsControllers.length > 0 && (
          <div className="mt-3 flex flex-wrap items-baseline gap-2 text-xs">
            <span className="text-10 font-semibold uppercase tracking-[0.06em] text-ink-4">
              DCS controller
            </span>
            <span className="flex flex-wrap gap-1.5">
              {dcsControllers.map((ctrl) => (
                <PlantHref
                  key={ctrl.id}
                  id={ctrl.id}
                  label="DcsController"
                  node={ctrl}
                  onClick={(event) => event.stopPropagation()}
                  className="rounded-md border border-transparent bg-bg-sunk px-2 py-0.5 font-medium text-ink-3 no-underline hover:border-brand hover:text-ink"
                >
                  {ctrl.name}
                </PlantHref>
              ))}
            </span>
          </div>
        )}
      </div>
    </header>
  )
}

/**
 * Inverse of the asset pages' compute chip: the Equipment / Line nodes
 * that are `MONITORED_BY` this compute (i.e. whose telemetry it serves).
 * Each tile deep-links back into the Equipment / Line tab so an operator
 * can hop from an OPC server / device to the plant gear it feeds.
 */
function MonitoredAssetsSection({
  assets
}: {
  assets: PlantMonitoredAsset[]
}): React.ReactElement | null {
  if (!assets || assets.length === 0) return null
  return (
    <section>
      <SectionHeader
        label="Monitored equipment & lines"
        subtitle={`${assets.length} ${assets.length === 1 ? 'asset' : 'assets'} served by this compute`}
      />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(16.25rem,1fr))]">
        {assets.map((asset) => (
          <MonitoredAssetTile key={`${asset.kind}:${asset.id}`} asset={asset} />
        ))}
      </div>
    </section>
  )
}

function MonitoredAssetTile({ asset }: { asset: PlantMonitoredAsset }): React.ReactElement {
  const Icon = asset.kind === 'line' ? LineIcon : equipmentIconFor(asset.type)
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(asset.id)
  return (
    <PlantHref
      id={asset.id}
      label={asset.kind === 'line' ? 'Line' : 'Equipment'}
      node={asset}
      className={sessionMarkClass(
        'flex min-w-0 items-center gap-2.5 rounded-lg border bg-card p-2.5 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground',
        mark
      )}
    >
      <span className="grid size-8 shrink-0 place-items-center rounded-md bg-bg-sunk text-ink-3">
        <Icon size={16} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="block min-w-0 flex-1 truncate text-13 font-semibold text-ink">
            {asset.name}
          </span>
          <SessionMarkAffordances mark={mark} />
        </span>
        <span className="block truncate font-mono text-11 text-ink-3">
          {asset.kind === 'line' ? 'Line' : (asset.type ?? 'Equipment')}
        </span>
      </span>
    </PlantHref>
  )
}

function isOpcProtocol(protocol: string | undefined): boolean {
  return protocol === 'opc-ua' || protocol === 'opc-da'
}

function TagProviderCard({
  provider,
  active,
  onClick
}: {
  provider: PlantTagProvider
  active: boolean
  onClick: () => void
}): React.ReactElement {
  const Icon = isOpcProtocol(provider.protocol) ? PlugIcon : TagIcon
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(provider.id)
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={sessionMarkClass(
        cn(
          'flex min-w-0 items-start gap-3 rounded-xl border bg-card p-3.5 text-left text-card-foreground shadow-sm transition-shadow',
          'cursor-pointer hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand',
          // Inset brand ring — stays visible over session-paint fills, no offset gap.
          active && 'ring-2 ring-inset ring-brand'
        ),
        mark
      )}
    >
      <span className="grid size-9 shrink-0 place-items-center rounded-md bg-bg-sunk text-ink-3">
        <Icon size={16} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-baseline gap-2">
          <span className="min-w-0 max-w-full truncate text-sm font-semibold text-ink">
            {provider.name}
          </span>
          <SessionMarkAffordances mark={mark} />
          {provider.protocol && <span className="text-11 text-ink-3">{provider.protocol}</span>}
        </div>
        {provider.url && (
          <div className="mt-0.5 truncate font-mono text-12 text-ink-3">{provider.url}</div>
        )}
        {provider.enabled === false && (
          <div className="mt-1.5">
            <Badge variant="outline">disabled</Badge>
          </div>
        )}
      </div>
    </button>
  )
}

function DatabaseCard({
  database,
  active,
  onClick
}: {
  database: PlantDatabase
  active: boolean
  onClick: (() => void) | undefined
}): React.ReactElement {
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(database.id)
  const className = sessionMarkClass(
    cn(
      'flex min-w-0 items-center gap-3 rounded-xl border bg-card px-3.5 py-2.5 text-left text-card-foreground shadow-sm transition-shadow',
      onClick &&
        'cursor-pointer hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand',
      active && 'ring-2 ring-inset ring-brand'
    ),
    mark
  )
  const body = (
    <>
      <span className="grid size-8 shrink-0 place-items-center rounded-md bg-bg-sunk text-ink-3">
        <DatabaseIcon size={14} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-baseline gap-2">
          <span className="min-w-0 max-w-full truncate text-sm font-semibold text-ink">
            {database.name}
          </span>
          <SessionMarkAffordances mark={mark} />
          {database.database_type && (
            <span className="text-11 text-ink-3">{database.database_type}</span>
          )}
          {database.enabled === false && <Badge variant="outline">disabled</Badge>}
        </div>
        {database.url && (
          <div className="mt-0.5 truncate font-mono text-12 text-ink-3">{database.url}</div>
        )}
      </div>
    </>
  )
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className}>
        {body}
      </button>
    )
  }
  return <div className={className}>{body}</div>
}

function ComputeGhostTile({
  ghost,
  kind
}: {
  ghost: PlantSessionGhostView
  kind: 'provider' | 'database'
}): React.ReactElement {
  const Icon = kind === 'database' ? DatabaseIcon : TagIcon
  return (
    <SessionGhostTile
      name={ghost.name ?? ghost.id}
      view={ghost.view}
      icon={
        <span className="grid size-9 shrink-0 place-items-center rounded-md bg-st-down-bg text-st-down">
          <Icon size={16} />
        </span>
      }
    />
  )
}

function SectionHeader({
  label,
  subtitle
}: {
  label: string
  subtitle?: string
}): React.ReactElement {
  return (
    <div className="mb-2 flex items-baseline gap-3">
      <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">{label}</h3>
      {subtitle && <span className="text-xs text-ink-3">{subtitle}</span>}
    </div>
  )
}

function DetailFallback({
  title,
  hint,
  muted
}: {
  title: string
  hint: string
  muted?: boolean
}): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
      {muted ? (
        <div className="text-sm text-ink-3">{title}</div>
      ) : (
        <Empty title={title} hint={hint} icon={WarningCircleIcon} />
      )}
    </div>
  )
}

export default ComputeHostDetailPage
